import React from 'react';
import { TrendingUp, Users, Clock, Award } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const ConclusionSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const metrics = [
    {
      icon: <TrendingUp className="w-12 h-12" />,
      value: '40%',
      label: t.conclusion.complaintReduction,
      description: t.conclusion.complaintReductionDesc,
      color: 'from-green-400 to-green-600',
    },
    {
      icon: <Clock className="w-12 h-12" />,
      value: '35%',
      label: t.conclusion.efficiencyImprovement,
      description: t.conclusion.efficiencyImprovementDesc,
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: <Users className="w-12 h-12" />,
      value: '100%',
      label: t.conclusion.complianceAssurance,
      description: t.conclusion.complianceAssuranceDesc,
      color: 'from-purple-400 to-purple-600',
    },
  ];

  const achievements = [
    t.conclusion.achievement1,
    t.conclusion.achievement2,
    t.conclusion.achievement3,
    t.conclusion.achievement4,
    t.conclusion.achievement5,
  ];

  return (
    <section
      id="conclusion"
      className="min-h-screen py-24 bg-gradient-to-br from-white via-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-2xl">
              <Award className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.conclusion.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] mx-auto rounded-full"></div>
          <p className="text-xl text-gray-600 mt-6 max-w-3xl mx-auto">
            {t.conclusion.description}
          </p>
        </div>

        {/* Expected Value Based on Past Achievements */}
        <div className="mb-16 bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-8 md:p-12 shadow-xl">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            {t.conclusion.expectedValue}
          </h3>
          <p className="text-lg text-gray-700 mb-8 text-center max-w-3xl mx-auto">
            {t.conclusion.expectedValueDesc}
          </p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              {/* Icon with Gradient Background */}
              <div className={`mb-6 w-20 h-20 rounded-2xl bg-gradient-to-br ${metric.color} flex items-center justify-center text-white shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                {metric.icon}
              </div>

              {/* Value */}
              <div className={`text-5xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent mb-2`}>
                {metric.value}
              </div>

              {/* Label */}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {metric.label}
              </h3>

              {/* Description */}
              <p className="text-gray-600">
                {metric.description}
              </p>

              {/* Decorative Element */}
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${metric.color} opacity-5 rounded-bl-full transform group-hover:opacity-10 transition-opacity duration-300`}></div>
            </div>
          ))}
        </div>

        {/* Key Achievements */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 shadow-xl mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t.conclusion.keyAchievements}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-full flex items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
                <p className="text-gray-700 text-lg">{achievement}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why I'm the Right Fit for Canva */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 md:p-12 shadow-xl mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            {t.conclusion.whyFit}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-lg flex items-center justify-center mr-4">
                  <span className="text-white font-bold text-xl">✓</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900">{t.conclusion.uniqueBridge.title}</h4>
              </div>
              <p className="text-gray-600">
                {t.conclusion.uniqueBridge.description}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-lg flex items-center justify-center mr-4">
                  <span className="text-white font-bold text-xl">✓</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900">{t.conclusion.provenTrack.title}</h4>
              </div>
              <p className="text-gray-600">
                {t.conclusion.provenTrack.description}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-lg flex items-center justify-center mr-4">
                  <span className="text-white font-bold text-xl">✓</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900">{t.conclusion.deepUnderstanding.title}</h4>
              </div>
              <p className="text-gray-600">
                {t.conclusion.deepUnderstanding.description}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-lg flex items-center justify-center mr-4">
                  <span className="text-white font-bold text-xl">✓</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900">{t.conclusion.completeCapability.title}</h4>
              </div>
              <p className="text-gray-600">
                {t.conclusion.completeCapability.description}
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-4">
              {t.conclusion.ctaTitle}
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl">
              {t.conclusion.ctaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:ivyxie.work@gmail.com"
                className="px-8 py-4 bg-white text-[#00C4CC] rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                {t.conclusion.getInTouch}
              </a>
              <a 
                href="https://linkedin.com/in/yuwei-xie"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white border-2 border-white rounded-full font-semibold hover:bg-white/30 transition-all duration-300"
              >
                {t.conclusion.viewLinkedIn}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConclusionSection;


